import { t } from "i18next"
import { DATE_FORMAT } from "constants"
import { regex } from "constants/regex"
import { stringValidationYup } from "helpers/validations"
import * as yup from "yup"

export const payorderFormProps = ({ referenceError, referenceValidity, isRequired = true }) => [
  {
    required: isRequired,
    name: "payorderBank",
    label: t("Bank"),
    hasFeedback: false,
    placeholder: t("Select bank"),
  },
  {
    required: isRequired,
    name: "payorderNumber",
    label: t("Pay Order Number"),
    hasFeedback: false,
    rules: {
      validator: (_, value) => {
        return referenceError || referenceValidity?.isValid === false
          ? Promise.reject(t("Pay order number is invalid"))
          : Promise.resolve()
      },
    },
  },
  {
    required: isRequired,
    name: "beneficiaryAccountNumberPayorder",
    label: t("Beneficiary Account Number"),
    hasFeedback: false,
  },
]

export const payorderFormChildren = ({
  setFieldsValue,
  optionList,
  getFieldValue,
  debouncedReferenceValidator,
  modeofPayment,
  payorderBank,
}) => {
  return [
    {
      type: "select",
      options: optionList?.bank,
      placeholder: t("Select bank"),
      getOptionValue: item => item.key,
      onChange: (value, option) => {
        setFieldsValue({ payorderBank: value, payorderNumber: null })
      },
    },
    {
      type: "text",
      placeholder: t("Enter number"),
      disabled: !!payorderBank ? false : true,
      onChange: e => {
        if (e.target.value !== "") {
          debouncedReferenceValidator({
            referenceNumber: e.target.value,
            bankKey: payorderBank,
            paymentModeKey: getFieldValue("modeOfPayment") ?? modeofPayment,
          })
        }
      },
    },
    {
      type: "text",
      placeholder: t("Enter account number"),
    },
  ]
}

export const depositSlipFormProps = (isRequired = true) => [
  {
    required: isRequired,
    name: "depositBank",
    label: t("Deposited Bank"),
    hasFeedback: false,
  },
  {
    required: isRequired,
    name: "depositSlipNumber",
    label: t("Deposit Slip Number"),
  },
  {
    required: isRequired,
    name: "depositDate",
    label: t("Deposit Date"),
    hasFeedback: false,
  },
  {
    required: isRequired,
    name: "accountNumber",
    label: t("Account Number"),
    hasFeedback: false,
  },
]

export const depositSlipFormChildren = ({ setFieldsValue, optionList }) => {
  return [
    {
      type: "select",
      options: optionList?.bank,
      placeholder: t("Select bank"),
      getOptionValue: item => item.key,
      onChange: (value, option) => {
        setFieldsValue({ depositBank: value })
      },
    },
    {
      type: "text",
      placeholder: t("Enter deposit slip number"),
    },
    {
      type: "date",
      disableFuture: true,
      showTime: false,
      allowClear: true,
      format: DATE_FORMAT,
    },
    {
      type: "text",
      placeholder: t("Enter account number"),
    },
  ]
}

export const onlinePaymentFormProps = (userPlatformKey, isRequired = true) => [
  {
    required: isRequired,
    name: "bank",
    label: t("From Bank"),
    hasFeedback: false,
  },
  {
    required: isRequired,
    name: "transactionNumber",
    label: t("Transaction Number"),
    hasFeedback: false,
  },
  {
    name: "fromAccountTitle",
    label: t("From Account Title"),
    hasFeedback: false,
  },
  {
    name: "fromAccountNumber",
    label: t("From Account Number"),
    hasFeedback: false,
  },
  {
    name: "beneficiaryAccountTitle",
    label: t("Beneficiary Account Title"),
    hasFeedback: false,
  },
  {
    ...(isRequired && { required: userPlatformKey === "zameen" }),
    name: "beneficiaryAccountNumber",
    label: t("Beneficiary Account Number"),
    hasFeedback: false,
  },
  {
    required: isRequired,
    name: "paymentType",
    label: t("Type"),
    hasFeedback: false,
    ...(!isRequired && { getOptionValue: item => item?.key }),
  },
  {
    name: "transactionDate",
    label: t("Transaction Date"),
    hasFeedback: false,
  },
]

export const onlinePaymentFormChildren = ({ setFieldsValue, optionList }) => {
  return [
    {
      type: "select",
      options: optionList?.bank,
      placeholder: t("Select bank"),
      getOptionValue: item => item.key,
      onChange: (value, option) => {
        setFieldsValue({ bank: value })
      },
    },
    {
      type: "text",
      placeholder: t("Enter transaction number"),
    },
    {
      type: "text",
      placeholder: t("Enter account title"),
    },
    {
      type: "text",
      placeholder: t("Enter account number"),
    },
    {
      type: "text",
      placeholder: t("Enter account title"),
    },
    {
      type: "text",
      placeholder: t("Enter account Number"),
    },
    {
      type: "select",
      options: optionList?.paymentMode,
      placeholder: t("Select type"),
      onChange: (value, option) => {
        setFieldsValue({ paymentType: option })
      },
    },
    {
      type: "date",
      disableFuture: true,
      showTime: false,
      allowClear: true,
      format: DATE_FORMAT,
    },
  ]
}

export const chequeFormProps = ({ referenceError, referenceValidity, isRequired = true }) => [
  {
    required: isRequired,
    name: "chequeBank",
    label: t("Bank"),
    hasFeedback: false,
  },
  {
    required: isRequired,
    name: "referenceNumber",
    label: t("Cheque Number"),
    hasFeedback: false,
    rules: {
      validator: (_, value) => {
        return referenceError || referenceValidity?.isValid === false
          ? Promise.reject(t("Cheque number is invalid"))
          : Promise.resolve()
      },
    },
  },
  {
    required: isRequired,
    name: "fromAccountNumber",
    label: t("From Account Number"),
    hasFeedback: false,
    rules: [
      {
        validator: (_, value) => {
          const schema = yup.object({
            fromAccountNumber: stringValidationYup(
              t("From account number is required"),
              regex.ksaOnlinePaymentRegex
            ).nullable(),
          })
          return schema.validate({ fromAccountNumber: value })
        },
      },
    ],
  },
  {
    required: isRequired,
    name: "paymentType",
    label: t("Type"),
    hasFeedback: false,
  },
  {
    name: "chequeDate",
    label: t("Cheque Date"),
    hasFeedback: false,
  },
]

export const chequeFormChildren = ({
  setFieldsValue,
  optionList,
  getFieldValue,
  debouncedReferenceValidator,
  modeofPayment,
  chequeBank,
  isRequired = true,
}) => {
  return [
    {
      type: "select",
      options: optionList?.bank,
      placeholder: t("Select bank"),
      getOptionValue: item => item.key,
      onChange: (value, option) => {
        setFieldsValue({ chequeBank: value, referenceNumber: null })
      },
    },
    {
      type: "text",
      placeholder: t("Enter number"),
      ...(isRequired && { disabled: !!chequeBank ? false : true }),
      onChange: e => {
        if (e.target.value !== "") {
          debouncedReferenceValidator({
            referenceNumber: e.target.value,
            bankKey: chequeBank,
            paymentModeKey: getFieldValue?.("modeOfPayment") ?? modeofPayment,
          })
        }
      },
    },
    {
      type: "text",
      placeholder: t("Enter account number"),
    },
    {
      type: "select",
      options: optionList?.paymentMode,
      placeholder: t("Select payment type"),
      ...(!isRequired && { getOptionValue: item => item?.key }),
      onChange: (value, option) => {
        setFieldsValue({ paymentType: option })
      },
    },
    {
      type: "date",
      disableFuture: true,
      showTime: false,
      allowClear: true,
      format: DATE_FORMAT,
    },
  ]
}

export const cardPaymentFormProps = (isRequired = true) => [
  {
    required: isRequired,
    name: "dateOfTransaction",
    label: t("Transaction Date"),
    hasFeedback: false,
  },
  {
    required: isRequired,
    name: "transactionId",
    label: t("Transaction ID"),
    hasFeedback: false,
  },
]

export const cardPaymentFormChildren = ({ setFieldsValue }) => {
  return [
    {
      type: "date",
      placeholder: t("Select transaction date"),
      disableFuture: true,
      allowClear: true,
      format: DATE_FORMAT,
      showTime: false,
      onChange: (value, dateString) => {
        setFieldsValue({ transactionDate: dateString })
      },
    },
    {
      type: "text",
      placeholder: t("Enter transaction ID"),
      onChange: e => {
        setFieldsValue({ transactionId: e.target.value })
      },
    },
  ]
}
